import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Lock, Phone, Mail, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getFAQ, getTestimonials } from '@/services/api';
import { siteConfig } from '@/data/mock';

const Partners = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [faqData, testimonialsData] = await Promise.all([
        getFAQ(),
        getTestimonials()
      ]);
      setFaqItems(faqData);
      setTestimonials(testimonialsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[hsl(215,15%,45%)]">Lade Daten...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)] pattern-bg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
              <Shield className="w-4 h-4 mr-2" />
              Unser Netzwerk
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Partner
            </h1>
          </div>
        </div>
      </section>

      {/* Discretion Message Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-[hsl(210,25%,90%)] shadow-lg">
            <CardContent className="p-10 md:p-16 text-center">
              <div className="w-20 h-20 rounded-full bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center mx-auto mb-8">
                <Lock className="w-10 h-10 text-[hsl(213,80%,35%)]" />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Diskretion ist unsere Priorität
              </h2>
              
              <p className="text-lg text-[hsl(215,15%,45%)] leading-relaxed mb-10 max-w-2xl mx-auto">
                Um die Privatsphäre unserer Partner und Mandanten zu schützen, verzichten wir auf eine öffentliche Auflistung unseres Netzwerks. Gerne stellen wir Ihnen unsere Auswahl an spezialisierten Rechtsanwälten und Beratungsstellen in einem persönlichen Gespräch vor.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white shadow-lg">
                  <Link to="/kontakt">
                    <Calendar className="w-5 h-5 mr-2" />
                    Termin vereinbaren
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-[hsl(213,80%,35%)] text-[hsl(213,80%,35%)]">
                  <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}>
                    <Phone className="w-5 h-5 mr-2" />
                    {siteConfig.phone}
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Trust indicators */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-xl bg-[hsl(160,60%,40%,0.1)] flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[hsl(160,60%,40%)]" />
              </div>
              <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Geprüfte Partner</h3>
              <p className="text-sm text-[hsl(215,15%,45%)]">
                Alle Partner werden von uns persönlich ausgewählt und geprüft.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-xl bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[hsl(213,80%,35%)]" />
              </div>
              <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">100% Diskret</h3>
              <p className="text-sm text-[hsl(215,15%,45%)]">
                Ihre Daten und Anfragen werden streng vertraulich behandelt.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-xl bg-[hsl(35,90%,50%,0.1)] flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[hsl(35,90%,45%)]" />
              </div>
              <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Persönliche Beratung</h3>
              <p className="text-sm text-[hsl(215,15%,45%)]">
                Wir finden gemeinsam den richtigen Ansprechpartner für Sie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Häufig gestellte Fragen
            </h2>
            <p className="text-lg text-[hsl(215,15%,45%)]">
              Antworten auf die wichtigsten Fragen
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item) => (
              <AccordionItem 
                key={item.id} 
                value={`item-${item.id}`}
                className="bg-white rounded-lg border border-[hsl(210,25%,90%)] px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-[hsl(215,25%,15%)] hover:text-[hsl(213,80%,35%)] hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[hsl(215,15%,45%)] pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
                Erfahrungsberichte
              </h2>
              <p className="text-lg text-[hsl(215,15%,45%)]">
                Was andere Väter über die Unterstützung sagen
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="card-hover border-[hsl(210,25%,90%)]">
                  <CardContent className="p-6">
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-[hsl(35,90%,50%)] fill-current" />
                      ))}
                    </div>
                    <p className="text-[hsl(215,25%,30%)] text-sm leading-relaxed mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center mr-2">
                        <span className="text-[hsl(213,80%,35%)] text-sm font-semibold">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-[hsl(215,25%,15%)] text-sm">{testimonial.name}</p>
                        <p className="text-xs text-[hsl(215,15%,45%)]">{testimonial.location}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[hsl(213,80%,35%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Wir sind für Sie da
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Vereinbaren Sie ein kostenloses Erstgespräch und erfahren Sie mehr über unser Netzwerk an Experten.
          </p>
          <Button asChild size="lg" className="bg-white text-[hsl(213,80%,35%)] hover:bg-white/90 shadow-lg text-base px-8">
            <Link to="/kontakt">
              Kontakt aufnehmen
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Partners;
